import { EventObject, Typestate } from 'xstate';
import { Role } from '../../types/role';
import { launchPageTypes } from '../../types/launchPageTypes';
import { NetworkError } from '../actions/genericActions';
import { ApplyRequestMachineContext, ApplyResponseMachineContext } from './shared/context/applyMachineContext';
import { BankDetailsMachineContext } from './shared/context/bankDetailsMachineContext';
import { CellPhoneMachineContext } from './shared/context/cellPhoneMachineContext';
import { CustomerSearchRequestMachineContext, CustomerSearchResponseMachineContext } from './shared/context/customerSearchMachineContext';
import { OrderIdMachineContext } from './shared/context/orderIdMachineContext';
import { OrderMachineContext } from './shared/context/orderMachineContext';
import { LeaseItem, SelectLeaseOptionErrors } from '../../gql/graphql';
import { CartItemModel } from './existingApplicationMachine.types';
import { LDClient } from 'launchdarkly-js-client-sdk';
import { RoleMachineContext } from './shared/context/roleMachineContext';

export interface EcommMachineMachineContext
  // needs these types to pass them between machines
  extends RoleMachineContext,
    OrderMachineContext,
    OrderIdMachineContext,
    CellPhoneMachineContext,
    ApplyRequestMachineContext,
    ApplyResponseMachineContext,
    CustomerSearchRequestMachineContext,
    CustomerSearchResponseMachineContext,
    BankDetailsMachineContext {
  // configs
  highestSupportedApplicationMilestone: number;
  launchPage: launchPageTypes | null;
  reviewPage: boolean;
  contractSummaryPageParam: boolean;
  merchantContractSummaryPageParam: boolean;

  // used to transfer state between machines
  customerLookupStartingPage?: string;
  existingApplicationStartingPage?: string;
  postDecisionStartingPage?: string; // OR-2926: forwarded to post-decision-machine as skipToPage; assigned in OR-2997
  longFormApplicationStartingPage?: string;
  entryPointPage: string;
  otbInitialState?: string;

  // used to transfer data between machines, other data fields are covered by the above MachineContexts
  skipApproval: boolean;
  selectLeaseOptionErrors?: SelectLeaseOptionErrors[];
  cart: CartItemModel[];

  didSkipOTP: boolean;

  // used to transfer errors to the error boundary
  networkError: NetworkError | null;

  validatedByTokenType?: ValidatedByTokenType;

  ldClient: LDClient;

  applicationItems: LeaseItem[]; // for m4 bandaid

  //TODO: Ford remove when we can get items from Application
  hasAppItems?: boolean;
  entrypointParam: string;
  entrypointSessionId?: string;
}

export enum EcommMachineStates {
  EntryPointV2 = 'EntryPointV2',
  EcommMachine = 'EcommMachine',
  Loading = 'Loading',
  EntryPoint = 'EntryPoint',
  ExistingApplication = 'ExistingApplication',
  CustomerLookupFlow = 'CustomerLookupFlow',
  VirtualCard = 'VirtualCard',
  NewApplication = 'NewApplication',
  AnotherTestState = 'AnotherTestState',
  OTB = 'OTB',
  Signed = 'Signed',
  Declined = 'Declined',
  LoanDeclined = 'LoanDeclined',
  LeaseDeclined = 'LeaseDeclined',
  LongFormApplication = 'LongFormApplication',
}

export interface EcommMachineMachineStatesSchema {
  [EcommMachineStates.EntryPointV2]: {};
  [EcommMachineStates.EcommMachine]: {};
  [EcommMachineStates.Loading]: {};
  [EcommMachineStates.ExistingApplication]: {};
  [EcommMachineStates.CustomerLookupFlow]: {};
  [EcommMachineStates.VirtualCard]: {};
  [EcommMachineStates.OTB]: {};
  [EcommMachineStates.NewApplication]: {};
  [EcommMachineStates.AnotherTestState]: {};
  [EcommMachineStates.Signed]: {};
  [EcommMachineStates.Declined]: {};
  [EcommMachineStates.LoanDeclined]: {};
  [EcommMachineStates.LeaseDeclined]: {};
  [EcommMachineStates.LongFormApplication]: {};
}

export interface EcommMachineMachineStates extends Typestate<EcommMachineMachineContext> {
  type: EcommMachineMachineStatesSchema;
}

export enum EcommMachineEvents {
  APP_IN_PROGRESS = 'AppInProgress',
  APP_FINISHED = 'AppFinished',
  VIRTUAL_CARD_APP = 'VirtualCardApp',
  CUSTOMER_LOOKUP_EVENT = 'CustomerLookupEvent',
  SKIP_OTP_EVENT = 'SkipOtpEvent',
  NEW_APPLICATION = 'NewApplication',
  EXISTING_APPLICATION = 'ExistingApplication',
  OTB_CUSTOMER = 'OtbCustomer',
  LONG_FORM = 'LongForm',
  NEXT = 'NEXT',
  BACK = 'BACK',
  DECLINE = 'Decline',
  ERROR = 'Error',
  PAYMENT_ERROR = 'PaymentError',
  NETWORK_ERROR = 'NETWORK_ERROR',
  PHONE_SUBMITTED = 'PHONE_SUBMITTED',
  TRACK = 'TRACK',
  PAGE = 'PAGE',
  IDENTIFY = 'IDENTIFY',
  RESTART_APPLICATION = 'RESTART_APPLICATION',
}

type EcommMachineEventsTypeSchema =
  | EcommMachineEvents.APP_IN_PROGRESS
  | EcommMachineEvents.APP_FINISHED
  | EcommMachineEvents.VIRTUAL_CARD_APP
  | EcommMachineEvents.CUSTOMER_LOOKUP_EVENT
  | EcommMachineEvents.SKIP_OTP_EVENT
  | EcommMachineEvents.NEW_APPLICATION
  | EcommMachineEvents.EXISTING_APPLICATION
  | EcommMachineEvents.LONG_FORM
  | EcommMachineEvents.NEXT
  | EcommMachineEvents.BACK
  | EcommMachineEvents.DECLINE
  | EcommMachineEvents.ERROR
  | EcommMachineEvents.OTB_CUSTOMER
  | EcommMachineEvents.PAYMENT_ERROR
  | EcommMachineEvents.NETWORK_ERROR
  | EcommMachineEvents.PHONE_SUBMITTED
  | EcommMachineEvents.TRACK
  | EcommMachineEvents.PAGE
  | EcommMachineEvents.IDENTIFY
  | EcommMachineEvents.RESTART_APPLICATION;

export interface EcommMachineMachineEvents extends EventObject {
  type: EcommMachineEventsTypeSchema;
  data?: any;
}

// export enum EcommMachineGuardsType {
//   EXISTING_CUSTOMER_FOUND = 'ExistingCustomerFound',
//   CUSTOMER_OTB = 'CustomerOTB',
//   CUSTOMER_DECLINED = 'CustomerDeclined',
//   CUSTOMER_NOT_FOUND = 'CustomerNotFound',
// }

// type EcommMachineGuardsTypeSchema =
//   | EcommMachineGuardsType.EXISTING_CUSTOMER_FOUND
//   | EcommMachineGuardsType.CUSTOMER_OTB
//   | EcommMachineGuardsType.CUSTOMER_NOT_FOUND
//   | EcommMachineGuardsType.CUSTOMER_DECLINED;

// export interface EcommMachineMachineGuards extends EventObject {
//   type: EcommMachineGuardsTypeSchema;
// }

// Moving this out of redux, putting information shared by multiple machines here
export interface BasicInfoModel {
  firstName: string | undefined;
  lastName: string | undefined;
  emailAddress: string | null | undefined;
}
export interface BasicInfoContinuedModel {
  birthDate: string | undefined;
  taxpayerIdentificationNumber?: string;
  tokenizedTaxId?: string;
  monthlyIncome?: string;
}

export enum ValidatedByTokenType {
  HYBRID = 'HYBRID',
  OOBA = 'OOBA',
  CONTRACT = 'CONTRACT',
}
